import { ChevronDown } from 'lucide-react';
import { colors } from '../../lib/theme';
import { LENS_LABELS } from '../../lib/lens-labels';
import type { ConvertedScreeningRow } from '../../services/api';
import type { FilterToggles } from '../../lib/screening-presets';

function collectEvidence(company: ConvertedScreeningRow) {
  const bdsEvidence =
    company.bds?.categories?.flatMap((cat) => cat.evidence || []) ||
    company.rawStatuses?.bds?.categories?.flatMap((cat) => cat.evidence || []) ||
    [];
  const defenseEvidence = company.lensReasons?.defense ?? [];
  const shariahEvidence = company.lensReasons?.shariah ?? [];
  const reasonsEvidence = company.reasons || [];

  const allCategoryEvidence = [...bdsEvidence, ...defenseEvidence, ...shariahEvidence];
  const uniqueReasons = reasonsEvidence.filter((reason: string) => {
    const isDuplicate = allCategoryEvidence.some((evidence: string) => {
      const reasonLower = reason.toLowerCase();
      const evidenceLower = evidence.toLowerCase();
      if (reasonLower === evidenceLower) return true;
      if (reasonLower.includes(evidenceLower) && evidenceLower.length > 20) return true;
      if (evidenceLower.includes(reasonLower) && reasonLower.length > 20) return true;
      return false;
    });
    return !isDuplicate;
  });

  const totalEvidence =
    bdsEvidence.length + defenseEvidence.length + shariahEvidence.length + uniqueReasons.length;

  return { bdsEvidence, defenseEvidence, shariahEvidence, uniqueReasons, totalEvidence };
}

function LensSummary({ text }: { text: string }) {
  return (
    <div
      className="mb-2 rounded-lg p-2.5"
      style={{ border: `1px solid ${colors.blue}40`, backgroundColor: `${colors.blue}14` }}
    >
      <div className="font-mono text-[10px] uppercase tracking-wide mb-1" style={{ color: colors.blue }}>
        AI summary
      </div>
      <p className="text-xs leading-relaxed" style={{ color: colors.textSecondary }}>
        {text}
      </p>
    </div>
  );
}

function EvidenceItem({
  text,
  accent,
}: {
  text: string;
  accent: string;
}) {
  return (
    <div
      className="text-[13px] leading-relaxed mb-1.5 p-3 rounded-[10px]"
      style={{
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${accent}`,
        color: colors.textSecondary,
      }}
    >
      {text}
    </div>
  );
}

export function EvidenceDetails({
  company,
  filters,
}: {
  company: ConvertedScreeningRow;
  filters: FilterToggles;
}) {
  const summaries = company.evidenceSummaries ?? {};
  const { bdsEvidence, defenseEvidence, shariahEvidence, uniqueReasons } = collectEvidence(company);

  return (
    <div className="evidence-details space-y-3 pt-1">
      {filters.bds.enabled && bdsEvidence.length === 0 && company.bds.overall === 'clean' && (
        <div className="text-[13px] leading-relaxed" style={{ color: colors.textMuted }}>
          <p>{LENS_LABELS.occupationNoMatchDetail}</p>
          <p className="mt-1 font-mono text-[11px]">{LENS_LABELS.occupationCheckedSources}</p>
        </div>
      )}
      {filters.bds.enabled && bdsEvidence.length > 0 && (
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-wide mb-2" style={{ color: colors.red }}>
            {LENS_LABELS.occupationEvidence} ({bdsEvidence.length}):
          </div>
          {summaries.bds && <LensSummary text={summaries.bds} />}
          {bdsEvidence.map((evidence: string, index: number) => (
            <EvidenceItem key={index} text={`• ${evidence}`} accent={colors.red} />
          ))}
        </div>
      )}

      {filters.defense && defenseEvidence.length > 0 && (
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-wide mb-2" style={{ color: colors.yellow }}>
            Defense
          </div>
          {summaries.defense && <LensSummary text={summaries.defense} />}
          {defenseEvidence.map((evidence: string, index: number) => (
            <EvidenceItem key={index} text={`• ${evidence}`} accent={colors.yellow} />
          ))}
        </div>
      )}

      {filters.shariah && shariahEvidence.length > 0 && (
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-wide mb-2" style={{ color: colors.blue }}>
            Shariah
          </div>
          {summaries.shariah && <LensSummary text={summaries.shariah} />}
          {shariahEvidence.map((evidence: string, index: number) => (
            <EvidenceItem key={index} text={`• ${evidence}`} accent={colors.blue} />
          ))}
        </div>
      )}

      {uniqueReasons.length > 0 && (
        <div>
          {uniqueReasons.map((reason: string, index: number) => {
            const isCompliance =
              (reason.toLowerCase().includes('compliant') &&
                !reason.toLowerCase().includes('non-compliant')) ||
              reason.toLowerCase().includes('passed');
            const isViolation =
              reason.toLowerCase().includes('non-compliant') ||
              reason.toLowerCase().includes('exceeds') ||
              reason.toLowerCase().includes('violation');
            const accent = isCompliance ? colors.green : isViolation ? colors.red : colors.borderLight;
            return <EvidenceItem key={index} text={reason} accent={accent} />;
          })}
        </div>
      )}
    </div>
  );
}

export function ExpandableEvidenceCell({
  company,
  filters,
  isExpanded,
  onToggle,
}: {
  company: ConvertedScreeningRow;
  filters: FilterToggles;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { totalEvidence } = collectEvidence(company);

  if (totalEvidence === 0) {
    if (filters.bds.enabled && company.bds.overall === 'clean') {
      return (
        <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
          {LENS_LABELS.occupationNotFlagged}
        </span>
      );
    }
    return (
      <span className="text-xs" style={{ color: colors.textFaint }}>
        No evidence
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1 text-xs whitespace-nowrap hover:opacity-80"
      style={{ color: colors.textSecondary }}
    >
      <span className="font-mono">
        [{totalEvidence} {totalEvidence === 1 ? 'item' : 'items'}]
      </span>
      <ChevronDown
        className="h-3.5 w-3.5 transition-transform"
        style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', color: colors.textMuted }}
      />
    </button>
  );
}
